"use client";

import { useEffect, useMemo, useState } from "react";

type ProductGalleryProps = {
  images: string[];
  productName: string;
  imageAlt?: string;
};

const PLACEHOLDER_IMAGE = "/placeholder-product.png";
const GALLERY_LABEL = "\u0413\u0430\u043b\u0435\u0440\u0435\u044f \u0442\u043e\u0432\u0430\u0440\u0430";
const PHOTO_LABEL = "\u0424\u043e\u0442\u043e";
const OPEN_PREVIEW_LABEL = "\u041e\u0442\u043a\u0440\u044b\u0442\u044c \u043f\u0440\u043e\u0441\u043c\u043e\u0442\u0440";
const CLOSE_PREVIEW_LABEL = "\u0417\u0430\u043a\u0440\u044b\u0442\u044c \u043f\u0440\u043e\u0441\u043c\u043e\u0442\u0440";
const PREVIOUS_LABEL = "\u041f\u0440\u0435\u0434\u044b\u0434\u0443\u0449\u0435\u0435 \u0444\u043e\u0442\u043e";
const NEXT_LABEL = "\u0421\u043b\u0435\u0434\u0443\u044e\u0449\u0435\u0435 \u0444\u043e\u0442\u043e";

export function ProductGallery({ images, productName, imageAlt }: ProductGalleryProps) {
  const initialGallery = useMemo(() => {
    const filtered = images.filter(Boolean);
    return filtered.length ? Array.from(new Set(filtered)) : [PLACEHOLDER_IMAGE];
  }, [images]);

  const [brokenImages, setBrokenImages] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const gallery = useMemo(() => {
    const filtered = initialGallery.filter((image) => !brokenImages.includes(image));
    return filtered.length ? filtered : [PLACEHOLDER_IMAGE];
  }, [brokenImages, initialGallery]);

  const activeImage = gallery[activeIndex] ?? gallery[0];
  const finalAlt = imageAlt || productName;
  const hasMultipleImages = gallery.length > 1;

  useEffect(() => {
    if (activeIndex > gallery.length - 1) {
      setActiveIndex(0);
    }
  }, [activeIndex, gallery.length]);

  useEffect(() => {
    if (!isPreviewOpen) {
      return undefined;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsPreviewOpen(false);
        return;
      }

      if (!hasMultipleImages) {
        return;
      }

      if (event.key === "ArrowLeft") {
        setActiveIndex((current) => (current === 0 ? gallery.length - 1 : current - 1));
      }

      if (event.key === "ArrowRight") {
        setActiveIndex((current) => (current === gallery.length - 1 ? 0 : current + 1));
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gallery.length, hasMultipleImages, isPreviewOpen]);

  function markBroken(src: string) {
    setBrokenImages((current) => (current.includes(src) ? current : [...current, src]));
  }

  function showPreviousImage() {
    setActiveIndex((current) => (current === 0 ? gallery.length - 1 : current - 1));
  }

  function showNextImage() {
    setActiveIndex((current) => (current === gallery.length - 1 ? 0 : current + 1));
  }

  return (
    <>
      <div className="product-gallery-layout">
        <div className="product-thumb-column" aria-label={GALLERY_LABEL} role="tablist">
          {gallery.map((image, index) => (
            <button
              aria-label={`${PHOTO_LABEL} ${index + 1}`}
              aria-selected={index === activeIndex}
              className={`thumb product-thumb ${index === activeIndex ? "product-thumb--active" : ""}`}
              key={`${image}-${index}`}
              onClick={() => setActiveIndex(index)}
              role="tab"
              type="button"
            >
              <img
                alt={`${finalAlt} — ${PHOTO_LABEL.toLowerCase()} ${index + 1}`}
                loading="lazy"
                onError={() => markBroken(image)}
                src={image}
              />
            </button>
          ))}
        </div>

        <div className="gallery-main product-gallery-main">
          <button
            aria-label={OPEN_PREVIEW_LABEL}
            className="product-gallery-main__button"
            onClick={() => setIsPreviewOpen(true)}
            type="button"
          >
            <img alt={finalAlt} onError={() => markBroken(activeImage)} src={activeImage} />
          </button>

          {hasMultipleImages ? (
            <>
              <button
                aria-label={PREVIOUS_LABEL}
                className="product-gallery-nav product-gallery-nav--prev"
                onClick={showPreviousImage}
                type="button"
              >
                <span aria-hidden="true">‹</span>
              </button>
              <button
                aria-label={NEXT_LABEL}
                className="product-gallery-nav product-gallery-nav--next"
                onClick={showNextImage}
                type="button"
              >
                <span aria-hidden="true">›</span>
              </button>
              <span className="product-gallery-count">
                {activeIndex + 1} / {gallery.length}
              </span>
            </>
          ) : null}
        </div>
      </div>

      {isPreviewOpen ? (
        <div
          aria-label={GALLERY_LABEL}
          className="product-gallery-preview"
          onClick={() => setIsPreviewOpen(false)}
          role="dialog"
        >
          <div
            className="product-gallery-preview__dialog"
            onClick={(event) => event.stopPropagation()}
            role="document"
          >
            <button
              aria-label={CLOSE_PREVIEW_LABEL}
              className="product-gallery-preview__close"
              onClick={() => setIsPreviewOpen(false)}
              type="button"
            >
              ×
            </button>

            {hasMultipleImages ? (
              <button
                aria-label={PREVIOUS_LABEL}
                className="product-gallery-preview__nav product-gallery-preview__nav--prev"
                onClick={showPreviousImage}
                type="button"
              >
                ‹
              </button>
            ) : null}

            <div className="product-gallery-preview__media">
              <img alt={finalAlt} onError={() => markBroken(activeImage)} src={activeImage} />
            </div>

            {hasMultipleImages ? (
              <button
                aria-label={NEXT_LABEL}
                className="product-gallery-preview__nav product-gallery-preview__nav--next"
                onClick={showNextImage}
                type="button"
              >
                ›
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
